/**
 * useRogyogAnalytics.js
 *
 * Centralised analytics hook for the Rogyog (Yoga for Health) section.
 * Mirrors the exact pattern of useBlogAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [RogyogAnalytics] prefix
 *   - react-ga4 used in production
 *   - Debounced search query tracking (600 ms)
 *
 * HOW TO USE:
 *   const analytics = useRogyogAnalytics();
 *   analytics.trackVideoPlay(video, category, subcat);
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [RogyogAnalytics] in real time. No GA4 dashboard needed during dev.
 */

import { useCallback, useRef } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// Mirrors useBlogAnalytics: excludes localhost AND 127.0.0.1
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values — keeps GA4 payload clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // Dev: grouped, coloured, table-formatted — mirrors [BlogAnalytics] style
    // eslint-disable-next-line no-console
    console.group(`%c[RogyogAnalytics] ${eventName}`, "color:#0F9D58;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ─── Simple debounce (no lodash dependency) ────────────────────────────────────
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useRogyogAnalytics = () => {

  // Debounced search — 600 ms matches useBlogAnalytics
  const debouncedSearchRef = useRef(
    debounce((query, resultsCount) => {
      if (!query.trim()) return;
      send("rogyog_search_query", {
        search_term:   query,
        results_count: resultsCount,
      });
      if (resultsCount === 0) {
        send("rogyog_search_no_results", { search_term: query });
      }
    }, 600)
  );

  // ── Page view ────────────────────────────────────────────────────────────────

  const trackPageView = useCallback((title, url) => {
    send("rogyog_page_view", {
      page_title:    title,
      page_location: url,
    });
  }, []);

  // ── Category & subcategory navigation ────────────────────────────────────────

  const trackCategorySelect = useCallback((category, categoryIndex) => {
    send("rogyog_category_select", {
      category_name:  category,
      category_index: categoryIndex,
    });
  }, []);

  const trackSubcategorySelect = useCallback((subcat, subcatIndex, category) => {
    send("rogyog_subcategory_select", {
      subcategory_name:  subcat,
      subcategory_index: subcatIndex,
      parent_category:   category,
    });
  }, []);

  // ── Video interactions ────────────────────────────────────────────────────────

  const trackVideoPlay = useCallback((video, category, subcat, source = "browse") => {
    send("rogyog_video_play", {
      video_id:       video?.asnVideoPath ?? video?.videoPath ?? "",
      video_name:     video?.subCatName ?? video?.name ?? "",
      video_duration: video?.asnVideoDuration ?? video?.duration ?? "",
      category,
      subcategory:    subcat,
      // source: 'browse' | 'thumbnail' | 'search' | 'mobile_list'
      play_source:    source,
    });
  }, []);

  const trackThumbnailClick = useCallback((video, category, subcat) => {
    send("rogyog_thumbnail_click", {
      video_id:    video?.asnVideoPath ?? "",
      video_name:  video?.subCatName ?? "",
      category,
      subcategory: subcat,
    });
  }, []);

  // ── Search interactions ───────────────────────────────────────────────────────

  const trackSearchQuery = useCallback((query, resultsCount) => {
    debouncedSearchRef.current(query, resultsCount);
  }, []);

  const trackSearchClear = useCallback(() => {
    send("rogyog_search_clear");
  }, []);

  const trackTagChipClick = useCallback((tag, isDeselect = false) => {
    send("rogyog_tag_chip_click", {
      tag_name:  tag,
      action:    isDeselect ? "deselect" : "select",
    });
  }, []);

  const trackSearchResultClick = useCallback((asana, query, activeTag) => {
    send("rogyog_search_result_click", {
      video_id:       asana?.videoPath ?? "",
      video_name:     asana?.name ?? "",
      category:       asana?.category ?? "",
      subcategory:    asana?.subCatName ?? "",
      search_term:    query ?? "",
      active_tag:     activeTag ?? "",
    });
  }, []);

  // ── Accordion interactions ────────────────────────────────────────────────────

  const trackAccordionToggle = useCallback((label, isOpen, video, category) => {
    send("rogyog_accordion_toggle", {
      accordion_label: label,
      action:          isOpen ? "open" : "close",
      video_name:      video?.subCatName ?? "",
      category,
    });
  }, []);

  // ── Search panel open/close ───────────────────────────────────────────────────

  const trackSearchPanelToggle = useCallback((isOpen) => {
    send("rogyog_search_panel_toggle", {
      action: isOpen ? "open" : "close",
    });
  }, []);

  return {
    trackPageView,
    trackCategorySelect,
    trackSubcategorySelect,
    trackVideoPlay,
    trackThumbnailClick,
    trackSearchQuery,
    trackSearchClear,
    trackTagChipClick,
    trackSearchResultClick,
    trackAccordionToggle,
    trackSearchPanelToggle,
  };
};

export default useRogyogAnalytics;
